
const g:any = window[ENV.projectName] = window[ENV.projectName] || {};

import { Component, Vue, Watch } from 'vue-property-decorator';

import { mainStore } from '../_store/main';

import { ShapeKeys, PointCloudDefinition } from '../_modules/webgl/PointCloud';

import PageComponent from '../_modules/PageComponent';
import MainGL from '../_modules/webgl/MainGL';
import AboutMemberItem from '../_modules/AboutMemberItem';


const AboutScrollState = {
  KV: 'KV',
  VISION: 'VISION',
  SERVICE: 'SERVICE',
  CONTACT: 'CONTACT',
} as const;

export const AboutGLDefinition: PointCloudDefinition = {
  KV: {
    key: 'KV',
    shapeKey: 'KV',
    isKV: true,
    order: 0,
    kvItems: [
      { key: '', shapeKey: ShapeKeys.HOUSE, order: 0 },
   ]
  },
  VISION  : { key: 'VISION'  , shapeKey: ShapeKeys.BRAIN   , order: 1 },
  SERVICE : { key: 'SERVICE' , shapeKey: ShapeKeys.IEGAO   , order: 2 },
  CONTACT : { key: 'CONTACT' , shapeKey: ShapeKeys.CONTACT , order: 3 },
};


export default class Main extends PageComponent {
  protected memberEl!: HTMLElement;
  protected messageEl!: HTMLElement;

  protected visionEl!: HTMLElement;
  protected visionImgEl!: HTMLElement;
  protected visionObserver!: IntersectionObserver;

  protected serviceEl!: HTMLElement;
  protected serviceImgEl!: HTMLElement;
  protected serviceObserver!: IntersectionObserver;

  constructor() {
    super();
    this.init();
  }

  get isKV      () { return this.scrollState === AboutScrollState.KV      }
  get isService () { return this.scrollState === AboutScrollState.SERVICE }
  get isVision  () { return this.scrollState === AboutScrollState.VISION  }
  get isContact () { return this.scrollState === AboutScrollState.CONTACT }

  protected async initObservers() {
    await this.wait(400);

    this.initPageKVObserver();
    this.initVisionObserver();
    this.initServiceObserver();
    this.initContactLinkObserver();
  }

  protected async initVision() {
    this.visionEl = this.bodyElm.querySelector('.js-aboutVision') as HTMLElement;
    this.visionImgEl = this.visionEl.querySelector('.js-aboutVisionImg') as HTMLElement;
  }

  protected initVisionObserver() {
    if(!window.IntersectionObserver || g.isGoogleBot) return;

    this.visionObserver = new IntersectionObserver((entries)=> {
      const entry = entries[0];
      if(entry.isIntersecting) {
        if(this.scrollState === '' ||
          (!this.isVision &&
            ((this.scrollDir === 1 && this.isKV || this.scrollDir === -1 && !this.isKV))
          )
        ) {
          this.scrollState = AboutScrollState.VISION;
          console.log('scroll state:', AboutScrollState.VISION);
          this.mainGL.setMode(AboutScrollState.VISION);

        }
      }
    }, {
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      rootMargin: '-10% 0px'
    });
    this.visionObserver.observe(this.visionEl);
  }

  protected async initService() {
    this.serviceEl = this.bodyElm.querySelector('.js-aboutService') as HTMLElement;
    this.serviceImgEl = this.serviceEl.querySelector('.js-aboutServiceImg') as HTMLElement;
  }

  protected initServiceObserver() {
    if(!window.IntersectionObserver || g.isGoogleBot) return;

    this.serviceObserver = new IntersectionObserver((entries)=> {
      const entry = entries[0];
      if(entry.isIntersecting) {
        if(this.scrollState === '' ||
          (!this.isService &&
            ((this.scrollDir === 1 && (this.isKV || this.isVision)) || (this.scrollDir === -1 && !this.isKV && !this.isVision))
          )
        ) {
          this.scrollState = AboutScrollState.SERVICE;
          console.log('scroll state:', AboutScrollState.SERVICE);
          this.mainGL.setMode(AboutScrollState.SERVICE);

        }
      }
    }, {
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
      rootMargin: '-10% 0px'
    });
    this.serviceObserver.observe(this.serviceEl);
  }

  protected async init(): Promise<void> {
    mainStore.setCurrentPageId('about');

    this.scrollToTweenObj = { value: window.scrollY || window.pageYOffset };

    await Promise.all([
      g.loadFontPromise,
      this.initMainGL(),
      this.initLoading(),
      this.initHeader(),
      this.initFooter(),
      this.initMenu(),
    ]);

    await this.initContents();

    this.initMediaQuery();
    this.initScroll();
    this.initResize();

    this.resizeHandler();

    mainStore.setScrolled(false);
    mainStore.setLoaded(true);

    this.initObservers();

    await this.pageKV.$nextTick();

    this.isLocked = false;
    this.onScroll();
    this.onResize();
    this.play();
  }

  protected async initContents(): Promise<void> {
    await Promise.all([
      this.initPageKV(),
      this.initVision(),
      this.initService(),
      this.initContact(),
      this.initMember(),
      this.initMessage(),
    ]);

    await super.initContents();

    // menu member
    this.menu.setClickMember(()=> {
      this.scrollToMember();
      mainStore.closeMenu();
    });
    this.menu.setClickMessage(()=> {
      this.scrollToMessage();
      mainStore.closeMenu();
    });

    // footer member
    (this.bodyElm.querySelector('.js-footerMember') as HTMLElement)?.addEventListener('click', (e: MouseEvent)=> {
      this.scrollToMember();
      e.preventDefault();
      e.stopImmediatePropagation();
    });
    (this.bodyElm.querySelector('.js-footerMessage') as HTMLElement)?.addEventListener('click', (e: MouseEvent)=> {
      this.scrollToMessage();
      e.preventDefault();
      e.stopImmediatePropagation();
    });
  }

  protected async initMessage() {
    this.messageEl = this.bodyElm.querySelector('.js-aboutMessage') as HTMLElement;
  }

  protected async initMember() {
    this.memberEl = this.bodyElm.querySelector('.js-aboutMember') as HTMLElement;

    const promises: Promise<any>[] = [];
    const els = this.bodyElm?.querySelectorAll<HTMLElement>('.js-aboutMemberItem');
    if(!els) return;
    for (let i = 0, l = els.length; i < l; i++) {
      const item: AboutMemberItem = new AboutMemberItem({ el: els[i] });
      promises.push(this.onScreenItemManager.initItem(item));
    }
    return Promise.all(promises);
  }

  protected scrollToMember(duration: number = 1.4) {
    this.scrollTo(g.getOffsetTop(this.memberEl), duration);
  }

  protected scrollToMessage(duration: number = 1.4) {
    this.scrollTo(g.getOffsetTop(this.messageEl), duration);
  }

  protected async initMainGL() {
    const mainGLEl = this.bodyElm.querySelector('.js-mainGL') as HTMLElement;

    this.mainGL = new MainGL<typeof AboutGLDefinition>(
      mainGLEl,
      mainGLEl.querySelector('canvas') as HTMLCanvasElement,
      AboutGLDefinition,
      'KV',
      false
    );

    await this.mainGL.init()
    .catch((e)=> {
      console.log(e);
      this.htmlElm.classList.add('is-noWebGL');
      mainStore.setNoWebGL(true);
    })
    this.mainGL.onResize();
  }

  protected onResize(): void {
    console.log('onResize');
    setTimeout(()=> {
      this.mainGL.onResize();
      this.updateGLScales();
    }, 10)
  }

  protected onScroll(): void {
    super.onScroll();
    this.mainGL.setScrollTop();
  }

  protected updateGLScales() {
    const kvImgEl = this.pageKV.getImgContainer()

    this.mainGL.setParams({
      KV: {
        imgSize: kvImgEl.offsetWidth || 1,
        offset: this.getOffsetImgItem(kvImgEl, true)
      },
      VISION: {
        imgSize: this.visionImgEl.offsetWidth,
        offset: this.getOffsetImgItem(this.visionImgEl)
      },
      SERVICE: {
        imgSize: this.serviceImgEl.offsetWidth,
        offset: this.getOffsetImgItem(this.serviceImgEl)
      },
      CONTACT: {
        imgSize: this.contactLinkImgEl.offsetWidth,
        offset: this.getOffsetImgItem(this.contactLinkImgEl)
      },
    })
  }

  public play(): void {
    super.play();

    this.mainGL.startLoop();
    this.updateGLScales();

    const hash = location.hash.replace('#', '');

    if(hash === 'member') {
      this.scrollToMember(0);
    } else if(hash === 'message') {
      this.scrollToMessage(0);
    }

    this.mainGL.play(()=> {
      this.isLocked = false;
      this.mainGL.setMode('KV');
      this.mainGL.setKVIndex(0);
      this.pageKV.play();
    }, false);
  }


}